import React, { useEffect, useState } from 'react';
import { CheckCircle2, Clock, Target } from 'lucide-react';
import * as gamesApi from '../../api/games';
import { formatCountdown } from '../../lib/formatDate';

/**
 * Where you stand this week in everything else you are playing.
 *
 * Status, not a to-do list. The one game with a pick outstanding and the soonest
 * deadline already leads this page as a deadline block, drawn from the pending actions
 * — so it is dropped from here rather than said twice at two different weights.
 *
 * The rest is deliberately not actionable at a glance: "Picked: Brighton" is the answer
 * to a question somebody opened the app to ask, and "next sheet not up yet" is the
 * answer to the other one. Neither is a thing to do.
 *
 * Quiet like everything else on this page. Nothing live renders nothing, and a failed
 * read is swallowed — a landing page should not lead with an error about a strip.
 */
function MyWeek({ excludeGameId, onOpenGame }) {
  const [week, setWeek] = useState([]);

  useEffect(() => {
    let live = true;
    gamesApi.getMyWeek()
      .then((rows) => {
        if (live) setWeek(rows || []);
      })
      .catch(() => {
        if (live) setWeek([]);
      });
    return () => {
      live = false;
    };
  }, []);

  const rows = week.filter((entry) => entry.gameId !== excludeGameId);
  if (rows.length === 0) return null;

  return (
    <section className="my-week">
      <h2 className="section-eyebrow">
        {excludeGameId ? 'Your other games this week' : 'Your games this week'}
      </h2>
      <div className="my-week-list">
        {rows.map((entry) => (
          <WeekRow key={entry.gameId} entry={entry} onOpenGame={onOpenGame} />
        ))}
      </div>
    </section>
  );
}

function WeekRow({ entry, onOpenGame }) {
  const view = describe(entry);
  const where = [entry.gameName, entry.leagueName].filter(Boolean).join(' · ');
  const Icon = view.icon;

  return (
    <button
      type="button"
      className={`week-row is-${view.tone}`}
      onClick={() => onOpenGame && onOpenGame(entry.gameId)}
    >
      <span className="week-icon"><Icon size={19} /></span>
      <span className="week-text">
        <span className="week-title">
          {where}
          {entry.gameWeek != null && <span className="week-no"> · Week {entry.gameWeek}</span>}
        </span>
        <span className={`week-state is-${view.tone}`}>{view.state}</span>
      </span>
    </button>
  );
}

/**
 * The wording, which lives here rather than on the server.
 *
 * Three states and the third is the one worth naming carefully: a game between cycles
 * has no week at all, which is not the same as a week you have not picked in. "You have
 * not picked" on a game with nothing to pick would send somebody looking for a screen
 * that is not there.
 */
function describe(entry) {
  if (entry.teamName) {
    return {
      icon: CheckCircle2,
      tone: 'in',
      state: entry.canPick ? `Picked: ${entry.teamName}` : `${entry.teamName} — waiting on the results`,
    };
  }
  if (entry.canPick) {
    return {
      icon: Target,
      tone: 'todo',
      state: `Pick needed — ${formatCountdown(entry.pickDeadline)}`,
    };
  }
  if (entry.weekStatus == null) {
    return { icon: Clock, tone: 'quiet', state: 'Next sheet not up yet' };
  }
  return { icon: Clock, tone: 'quiet', state: 'Waiting on the results' };
}

export default MyWeek;
